// @ts-check
/**
 * App feedback screenshots: how many, what they are called, and where they
 * live. ONE copy, imported by the form (appFeedback.ts, FeedbackScreenshots.tsx),
 * the submit function (netlify/functions/feedbackNotify.mjs) and the daily
 * feed (netlify/functions/feedbackFeed.mjs), so the client and the server can
 * never disagree about a path. shared/storage.rules checks the same names.
 *
 * Bobbie, DECISIONS 123 item 12 (three families asked for more than one):
 * "Yes, up to 5".
 *
 * THE SHAPE, AND WHY IT KEEPS THE OLD ONE. A report from before this held one
 * file named `screenshot`, recorded as appFeedbackPrivate.screenshotPath.
 * Every older reader (the Firestore finalize rule, the Director's preview, a
 * cached tab still running the old bundle) knows only that field and that
 * name. So the FIRST file keeps both, exactly, and the rest are added beside
 * it: `screenshot-2` up to `screenshot-5`, listed in a new screenshotPaths
 * array that also holds the first. A report with no list is an older report,
 * and it has exactly its one screenshotPath.
 */

/** Bobbie's number. The storage rules and the server refuse a sixth. */
export const FEEDBACK_SCREENSHOT_LIMIT = 5;

/** The same cap shared/storage.rules isImageUpload() enforces (strictly under). */
export const FEEDBACK_SCREENSHOT_MAX_BYTES = 8 * 1024 * 1024;

const PATH_PREFIX = 'app-feedback-screenshots';

/**
 * The first file keeps the one name every older reader knows.
 * @param {number} index zero-based
 */
export function feedbackScreenshotFileName(index) {
  return index === 0 ? 'screenshot' : `screenshot-${index + 1}`;
}

/**
 * Every storage path a report with `count` screenshots may hold, in order.
 * @param {string} uid
 * @param {string} reportId
 * @param {number} count
 * @returns {string[]}
 */
export function feedbackScreenshotPaths(uid, reportId, count) {
  const safe = Number.isInteger(count)
    ? Math.max(0, Math.min(FEEDBACK_SCREENSHOT_LIMIT, count))
    : 0;
  return Array.from(
    { length: safe },
    (_, index) => `${PATH_PREFIX}/${uid}/${reportId}/${feedbackScreenshotFileName(index)}`,
  );
}

/**
 * The submit request's screenshot count, or null when the request is not
 * honest. A tab still running the older bundle sends hasScreenshot alone,
 * which has always meant exactly one, so a missing count is read that way.
 * @param {unknown} hasScreenshot
 * @param {unknown} rawCount
 * @returns {number | null}
 */
export function cleanFeedbackScreenshotCount(hasScreenshot, rawCount) {
  if (typeof hasScreenshot !== 'boolean') return null;
  if (rawCount === undefined) return hasScreenshot ? 1 : 0;
  if (typeof rawCount !== 'number' || !Number.isInteger(rawCount)) return null;
  if (!hasScreenshot) return rawCount === 0 ? 0 : null;
  return rawCount >= 1 && rawCount <= FEEDBACK_SCREENSHOT_LIMIT ? rawCount : null;
}

/**
 * The paths a private feedback record says it holds. A newer record lists
 * them all, first included; an older record has only its screenshotPath. A
 * list that does not start with screenshotPath is not trusted past it.
 * @param {{ screenshotPath?: unknown, screenshotPaths?: unknown } | null | undefined} details
 * @returns {string[]}
 */
export function storedFeedbackScreenshotPaths(details) {
  if (!details || typeof details !== 'object') return [];
  const first = typeof details.screenshotPath === 'string' && details.screenshotPath
    ? details.screenshotPath
    : '';
  if (!first) return [];
  const listed = Array.isArray(details.screenshotPaths)
    ? details.screenshotPaths.filter((path) => typeof path === 'string' && path.length > 0)
    : [];
  if (listed.length === 0 || listed[0] !== first) return [first];
  return listed.slice(0, FEEDBACK_SCREENSHOT_LIMIT);
}

/**
 * How many screenshots a public report really has attached: 0 until the
 * paired finalize write lands (screenshotAttached), 1 for every report from
 * before this (no count was ever written), else the count the server wrote.
 * @param {{ screenshotAttached?: unknown, screenshotCount?: unknown } | null | undefined} report
 * @returns {number}
 */
export function attachedFeedbackScreenshotCount(report) {
  if (!report || report.screenshotAttached !== true) return 0;
  const count = report.screenshotCount;
  return typeof count === 'number'
    && Number.isInteger(count)
    && count >= 1
    && count <= FEEDBACK_SCREENSHOT_LIMIT
    ? count
    : 1;
}

/**
 * True for a file the storage rules will accept: an image, under 8 MB.
 * @param {{ type?: unknown, size?: unknown }} file
 */
export function isFeedbackScreenshotFile(file) {
  return typeof file?.type === 'string'
    && file.type.startsWith('image/')
    && typeof file.size === 'number'
    && file.size < FEEDBACK_SCREENSHOT_MAX_BYTES;
}

/** @param {unknown} name */
function shortName(name) {
  const text = typeof name === 'string' && name.trim() ? name.trim() : 'That file';
  return text.length > 40 ? `${text.slice(0, 37)}...` : text;
}

/**
 * Add what was just picked to what is already there, in order, and say in
 * plain words what was left out and why. Nothing is ever silently dropped:
 * a file that is not an image, one that is too large, and every one past
 * the fifth each earn a sentence.
 * @template {{ name?: unknown, type?: unknown, size?: unknown }} T
 * @param {readonly T[]} current
 * @param {readonly T[]} picked
 * @returns {{ files: T[], message: string | null }}
 */
export function addFeedbackScreenshots(current, picked) {
  const files = [...current];
  const notImages = [];
  const tooLarge = [];
  let overLimit = 0;
  let added = 0;
  for (const file of picked) {
    if (typeof file?.type !== 'string' || !file.type.startsWith('image/')) {
      notImages.push(file);
      continue;
    }
    if (!isFeedbackScreenshotFile(file)) {
      tooLarge.push(file);
      continue;
    }
    if (files.length >= FEEDBACK_SCREENSHOT_LIMIT) {
      overLimit += 1;
      continue;
    }
    files.push(file);
    added += 1;
  }

  const parts = [];
  if (notImages.length === 1) {
    parts.push(`"${shortName(notImages[0]?.name)}" is not an image, so it was left out.`);
  } else if (notImages.length > 1) {
    parts.push(`${notImages.length} files are not images, so they were left out.`);
  }
  if (tooLarge.length === 1) {
    parts.push(`"${shortName(tooLarge[0]?.name)}" is too large. Each screenshot must be under 8 MB, so it was left out.`);
  } else if (tooLarge.length > 1) {
    parts.push(`${tooLarge.length} images are too large. Each screenshot must be under 8 MB, so they were left out.`);
  }
  if (overLimit > 0) {
    parts.push(added > 0
      ? `You can add up to ${FEEDBACK_SCREENSHOT_LIMIT} screenshots, so ${added} ${added === 1 ? 'was' : 'were'} added and ${overLimit} ${overLimit === 1 ? 'was' : 'were'} left out.`
      : `You can add up to ${FEEDBACK_SCREENSHOT_LIMIT} screenshots. Remove one to add another.`);
  }
  return { files, message: parts.length > 0 ? parts.join(' ') : null };
}
